import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { uploadImage, fullUrl } from "../lib/uploads";
import { toast } from "sonner";
import {
  CaretLeft, CaretRight, CheckCircle, Camera, MagnifyingGlass, X as XIcon,
  House, Buildings, Storefront, ShieldCheck, Lightning, Calendar, CurrencyGbp, MapPin,
} from "@phosphor-icons/react";

const PROPERTY_TYPES = [
  { value: "house", label: "House", Icon: House },
  { value: "flat", label: "Flat / Apartment", Icon: Buildings },
  { value: "bungalow", label: "Bungalow", Icon: House },
  { value: "commercial", label: "Commercial", Icon: Storefront },
  { value: "other", label: "Other", Icon: House },
];

const WHEN_OPTIONS = [
  { value: "asap", label: "ASAP", emoji: "🚨" },
  { value: "this_week", label: "This week", emoji: "📅" },
  { value: "this_month", label: "Within a month", emoji: "🗓️" },
  { value: "flexible", label: "Flexible", emoji: "⏳" },
];

const BUDGET_RANGES = [
  { value: "under-100", label: "Under £100" },
  { value: "100-250", label: "£100 – £250" },
  { value: "250-500", label: "£250 – £500" },
  { value: "500-1000", label: "£500 – £1,000" },
  { value: "1000-2500", label: "£1,000 – £2,500" },
  { value: "2500+", label: "£2,500+" },
  { value: "unsure", label: "Not sure yet" },
];

const POSTCODE_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i;

const isValidUKPhone = (raw) => {
  if (!raw) return false;
  const digits = String(raw).replace(/[\s\-().]/g, "");
  // Accepts +44… or 0… UK mobile/landline
  return /^(\+?44|0)\d{9,12}$/.test(digits);
};

export default function PostJob() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [trades, setTrades] = useState([]);
  const [tradeQuery, setTradeQuery] = useState("");
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createdJobId, setCreatedJobId] = useState(null);
  const [form, setForm] = useState({
    trade: "",
    title: "",
    description: "",
    photos: [],
    property_type: "",
    when_needed: "",
    budget_range: "",
    additional_notes: "",
    postcode: "",
    search_radius_miles: 20,
    visibility: "all_verified",
    contact_preference: "messages",
  });

  useEffect(() => {
    api.get("/meta/trades").then(({ data }) => setTrades(data || [])).catch(() => {});
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const filteredTrades = useMemo(() => {
    const q = tradeQuery.trim().toLowerCase();
    let list = trades;
    if (q) list = list.filter((t) => t.name.toLowerCase().includes(q));
    return showAllTrades || q ? list : list.slice(0, 8);
  }, [trades, tradeQuery, showAllTrades]);

  const selectedTrade = trades.find((t) => t.slug === form.trade);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - form.photos.length;
    if (remaining <= 0) {
      toast.error("Max 6 photos");
      return;
    }
    try {
      const urls = [];
      for (const f of files.slice(0, remaining)) {
        const url = await uploadImage(f);
        urls.push(url);
      }
      update({ photos: [...form.photos, ...urls] });
      toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const canContinue = useMemo(() => {
    switch (step) {
      case 1: return !!form.trade;
      case 2: return form.description.trim().length >= 10;
      case 3: return true;
      case 4: return POSTCODE_RE.test(form.postcode.trim());
      case 5: return true;
      case 6: return isValidUKPhone(form.contact_phone);
      default: return false;
    }
  }, [step, form]);

  const next = () => setStep((s) => Math.min(7, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", {
        title: (form.title.trim() || (selectedTrade?.name + " — " + form.postcode.toUpperCase())).slice(0, 120),
        description: form.description.trim(),
        trade: form.trade,
        postcode: form.postcode.trim().toUpperCase(),
        property_type: form.property_type || null,
        when_needed: form.when_needed || null,
        budget_range: form.budget_range || null,
        additional_notes: form.additional_notes.trim() || null,
        search_radius_miles: form.search_radius_miles,
        visibility: form.visibility,
        contact_preference: form.contact_preference,
        contact_phone: form.contact_phone.trim(),
        photos: form.photos,
      });
      setCreatedJobId(data.job_id);
      setStep(7);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-5 py-6 sm:py-10">
        {step < 7 && (
          <>
            <div className="flex items-center justify-between">
              <button onClick={step === 1 ? () => navigate(-1) : back}
                      className="text-[var(--brand-green)] p-2 -ml-2 rounded-md hover:bg-[#F4FDF7]"
                      data-testid="post-job-back">
                <CaretLeft size={22} weight="bold" />
              </button>
              <h1 className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>Post a Job</h1>
              <button onClick={() => navigate("/dashboard")}
                      className="text-sm text-[var(--text-muted)] hover:text-[var(--brand-green)]"
                      data-testid="post-job-cancel">Cancel</button>
            </div>
            <Progress step={step} total={6} />
          </>
        )}

        {step === 1 && (
          <Step1 trades={filteredTrades} value={form.trade} q={tradeQuery} setQ={setTradeQuery}
                 showAll={showAllTrades} setShowAll={setShowAllTrades}
                 onSelect={(slug) => update({ trade: slug })} />
        )}
        {step === 2 && (
          <Step2 description={form.description} setDescription={(v) => update({ description: v })}
                 photos={form.photos} onPhotos={onPhotos}
                 onRemovePhoto={(i) => update({ photos: form.photos.filter((_, idx) => idx !== i) })} />
        )}
        {step === 3 && (
          <Step3 form={form} update={update} selectedTradeName={selectedTrade?.name || "Job"} />
        )}
        {step === 4 && (
          <Step4 postcode={form.postcode} setPostcode={(v) => update({ postcode: v })}
                 radius={form.search_radius_miles} setRadius={(v) => update({ search_radius_miles: v })} />
        )}
        {step === 5 && (
          <Step5 form={form} selectedTrade={selectedTrade} />
        )}
        {step === 6 && (
          <Step6 form={form} update={update} />
        )}
        {step === 7 && (
          <Step7 jobId={createdJobId} navigate={navigate} />
        )}

        {step < 6 && (
          <button onClick={next} disabled={!canContinue}
                  className="mt-6 w-full bg-[var(--brand-green)] text-white font-bold py-3.5 rounded-xl disabled:opacity-40 shadow-lg shadow-emerald-900/10"
                  data-testid={`step-${step}-next`}>
            Next
          </button>
        )}
        {step === 6 && (
          <button onClick={submit} disabled={busy}
                  className="mt-6 w-full bg-[var(--brand-green)] text-white font-bold py-3.5 rounded-xl disabled:opacity-40 shadow-lg shadow-emerald-900/10"
                  data-testid="post-job-submit">
            {busy ? "Posting…" : "Post Job"}
          </button>
        )}
      </div>
    </Layout>
  );
}

function Progress({ step, total }) {
  return (
    <div className="mt-3 flex items-center gap-1.5" data-testid="post-job-progress">
      {Array.from({ length: total }).map((_, i) => {
        const filled = i + 1 <= step;
        return (
          <div key={i} className="flex-1 h-1.5 rounded-full transition-colors"
               style={{ background: filled ? "var(--brand-green)" : "#E4E2DC" }} />
        );
      })}
    </div>
  );
}

function Step1({ trades, value, q, setQ, showAll, setShowAll, onSelect }) {
  return (
    <section className="mt-5" data-testid="post-job-step-1">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>What do you need help with?</h2>
      <p className="text-sm text-[var(--text-muted)] mt-1">Select a category that best describes your job.</p>
      <div className="mt-4 relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 57 trades…"
               className="w-full pl-10 pr-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white"
               data-testid="step1-search" />
      </div>
      <div className="mt-3 space-y-1.5">
        {trades.map((t) => {
          const selected = t.slug === value;
          return (
            <button key={t.slug} type="button" onClick={() => onSelect(t.slug)}
                    className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border transition text-left ${
                      selected ? "bg-[#F4FDF7] border-[var(--brand-green)]" : "border-[var(--border-stone)] hover:bg-[#FAF8F2]"
                    }`}
                    data-testid={`step1-trade-${t.slug}`}>
              <span className="font-semibold text-[#1F4733]">{t.name}</span>
              {selected && <CheckCircle size={20} weight="fill" className="text-[var(--brand-green)] flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {!showAll && !q && (
        <button type="button" onClick={() => setShowAll(true)}
                className="mt-3 w-full py-2.5 rounded-xl border border-[var(--border-stone)] text-sm font-semibold text-[var(--brand-green)] hover:bg-[#F4FDF7]"
                data-testid="step1-see-all">
          See all categories
        </button>
      )}
    </section>
  );
}

function Step2({ description, setDescription, photos, onPhotos, onRemovePhoto }) {
  return (
    <section className="mt-5" data-testid="post-job-step-2">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Tell us more about your job</h2>
      <p className="text-sm text-[var(--text-muted)] mt-1">Provide a clear description so professionals can understand what you need.</p>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={6}
        maxLength={500}
        placeholder="e.g. Need someone to plaster and skim our living room and hallway walls. Some patches and cracks need fixing. Ready to paint afterwards."
        className="mt-4 w-full px-3 py-3 rounded-xl border border-[var(--border-stone)] bg-white resize-y focus:border-[var(--brand-green)] focus:ring-2 focus:ring-[var(--brand-green)]/10 outline-none"
        data-testid="step2-description"
      />
      <div className="text-right text-xs text-[var(--text-muted)] mt-1">{description.length}/500</div>

      <h3 className="mt-5 text-sm font-bold text-[var(--brand-green)]">Add Photos (Optional)</h3>
      <p className="text-xs text-[var(--text-muted)] mt-1">Add photos to help professionals understand the job better.</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={p} className="relative aspect-square rounded-lg overflow-hidden border border-[var(--border-stone)]">
            <img src={fullUrl(p)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onRemovePhoto(i)}
                    className="absolute top-1 right-1 bg-white/95 rounded-md w-6 h-6 flex items-center justify-center shadow"
                    data-testid={`step2-remove-photo-${i}`} aria-label="Remove photo">
              <XIcon size={12} weight="bold" />
            </button>
          </div>
        ))}
        {photos.length < 6 && (
          <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border-stone)] flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAF8F2] text-[var(--text-muted)]" data-testid="step2-photo-upload">
            <Camera size={20} weight="duotone" />
            <span className="text-[10px] mt-1 font-semibold">Add more</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
          </label>
        )}
      </div>
    </section>
  );
}

function Step3({ form, update, selectedTradeName }) {
  return (
    <section className="mt-5 space-y-5" data-testid="post-job-step-3">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Job details</h2>
      <p className="text-sm text-[var(--text-muted)] -mt-3">Let us know a few more details.</p>

      <label className="block">
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">Job Title (Optional)</span>
        <input value={form.title} onChange={(e) => update({ title: e.target.value })}
               placeholder={`${selectedTradeName} job`}
               className="mt-1 w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white"
               data-testid="step3-title" />
      </label>

      <div>
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1"><House size={12} weight="duotone" /> Property Type</span>
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
          {PROPERTY_TYPES.map((p) => {
            const sel = form.property_type === p.value;
            return (
              <button key={p.value} type="button" onClick={() => update({ property_type: sel ? "" : p.value })}
                      className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl border transition text-xs font-semibold ${
                        sel ? "bg-[#F4FDF7] border-[var(--brand-green)] text-[var(--brand-green)]" : "border-[var(--border-stone)] text-[var(--text-main)] hover:bg-[#FAF8F2]"
                      }`}
                      data-testid={`step3-property-${p.value}`}>
                <p.Icon size={20} weight="duotone" />
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1"><Calendar size={12} weight="duotone" /> When do you need it done?</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {WHEN_OPTIONS.map((w) => {
            const sel = form.when_needed === w.value;
            return (
              <button key={w.value} type="button" onClick={() => update({ when_needed: sel ? "" : w.value })}
                      className={`flex items-center gap-2 px-3 py-3 rounded-xl border transition text-sm font-semibold ${
                        sel ? "bg-[#F4FDF7] border-[var(--brand-green)] text-[var(--brand-green)]" : "border-[var(--border-stone)] hover:bg-[#FAF8F2]"
                      }`}
                      data-testid={`step3-when-${w.value}`}>
                <span>{w.emoji}</span>
                {w.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1"><CurrencyGbp size={12} weight="duotone" /> Budget (Optional)</span>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {BUDGET_RANGES.map((b) => {
            const sel = form.budget_range === b.value;
            return (
              <button key={b.value} type="button" onClick={() => update({ budget_range: sel ? "" : b.value })}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition ${
                        sel ? "bg-[#F4FDF7] border-[var(--brand-green)] text-[var(--brand-green)]" : "border-[var(--border-stone)] hover:bg-[#FAF8F2]"
                      }`}
                      data-testid={`step3-budget-${b.value}`}>
                {b.label}
              </button>
            );
          })}
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">Additional Notes (Optional)</span>
        <textarea value={form.additional_notes} onChange={(e) => update({ additional_notes: e.target.value })}
                  rows={2} maxLength={300}
                  placeholder="e.g. Ground floor. Easy parking available."
                  className="mt-1 w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white resize-y"
                  data-testid="step3-notes" />
      </label>
    </section>
  );
}

function Step4({ postcode, setPostcode, radius, setRadius }) {
  return (
    <section className="mt-5" data-testid="post-job-step-4">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Where do you need professionals?</h2>
      <p className="text-sm text-[var(--text-muted)] mt-1">We&apos;ll show your job to verified professionals within your selected area.</p>

      <div className="mt-5 rounded-2xl overflow-hidden bg-[#F4FDF7] border border-[#CBE7D6] flex items-center justify-center h-44 sm:h-52 relative">
        <MapPin size={48} weight="duotone" className="text-[var(--brand-green)]" />
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-4 border-[var(--brand-orange)]/40 bg-[var(--brand-orange)]/10" />
        </div>
      </div>

      <label className="mt-5 block">
        <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">Your postcode</span>
        <input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())}
               placeholder="M25 1AB"
               className="mt-1 w-full px-3 py-3 rounded-md border border-[var(--border-stone)] bg-white text-lg font-semibold tracking-wider uppercase"
               data-testid="step4-postcode" />
      </label>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">Search radius</span>
          <span className="font-display font-extrabold text-lg" style={{ color: "var(--brand-orange)" }} data-testid="step4-radius-value">{radius} miles</span>
        </div>
        <input type="range" min={5} max={50} step={5} value={radius}
               onChange={(e) => setRadius(Number(e.target.value))}
               className="w-full mt-2 accent-[var(--brand-orange)]"
               data-testid="step4-radius-slider" />
        <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
          {[5, 10, 20, 30, 50].map((n) => <span key={n}>{n}</span>)}
        </div>
      </div>
    </section>
  );
}

function Step5({ form, selectedTrade }) {
  const whenLabel = WHEN_OPTIONS.find((w) => w.value === form.when_needed)?.label || "Flexible";
  const budgetLabel = BUDGET_RANGES.find((b) => b.value === form.budget_range)?.label || "Not specified";
  return (
    <section className="mt-5" data-testid="post-job-step-5">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Review your job</h2>
      <p className="text-sm text-[var(--text-muted)] mt-1">Check your details before posting.</p>

      <div className="mt-5 brand-card p-4 divide-y divide-[var(--border-stone)]">
        <ReviewRow label="Category" value={selectedTrade?.name || form.trade} />
        {form.title && <ReviewRow label="Job Title" value={form.title} />}
        <ReviewRow label="Description" value={form.description} multiline />
        <ReviewRow label="Location" value={`${form.postcode} · within ${form.search_radius_miles} miles`} />
        <ReviewRow label="When" value={whenLabel} />
        <ReviewRow label="Budget" value={budgetLabel} />
        {form.property_type && <ReviewRow label="Property" value={form.property_type} />}
        {form.additional_notes && <ReviewRow label="Notes" value={form.additional_notes} multiline />}
        {form.photos.length > 0 && (
          <div className="py-3">
            <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2">Photos</div>
            <div className="flex gap-2 flex-wrap">
              {form.photos.map((p) => (
                <img key={p} src={fullUrl(p)} alt="" className="w-16 h-16 rounded-md object-cover border border-[var(--border-stone)]" />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewRow({ label, value, multiline }) {
  return (
    <div className="py-2.5 first:pt-1 last:pb-1">
      <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 text-sm text-[var(--text-main)] ${multiline ? "whitespace-pre-line" : "capitalize"}`}>{value}</div>
    </div>
  );
}

function Step6({ form, update }) {
  const phoneOk = isValidUKPhone(form.contact_phone);
  return (
    <section className="mt-5 space-y-5" data-testid="post-job-step-6">
      <h2 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Set your preferences</h2>
      <p className="text-sm text-[var(--text-muted)] -mt-3">Choose who can see and respond to your job.</p>

      <div>
        <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2">Who can view this job?</div>
        <RadioRow value="all_verified" selected={form.visibility === "all_verified"} label="All verified professionals"
                  onClick={() => update({ visibility: "all_verified" })} testId="step6-visibility-all" />
        <RadioRow value="matched_only" selected={form.visibility === "matched_only"} label="Let MyHandyHub match me"
                  onClick={() => update({ visibility: "matched_only" })} testId="step6-visibility-match" />
      </div>

      <div>
        <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2">How do you want to be contacted?</div>
        <RadioRow value="messages" selected={form.contact_preference === "messages"} label="In-app messages"
                  onClick={() => update({ contact_preference: "messages" })} testId="step6-contact-msg" />
        <RadioRow value="phone" selected={form.contact_preference === "phone"} label="Phone call"
                  onClick={() => update({ contact_preference: "phone" })} testId="step6-contact-phone" />
      </div>

      {/* Contact phone — required, shared only with the accepted trade */}
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-2 block" htmlFor="contact-phone">
          Your phone number <span style={{ color: "var(--brand-orange)" }}>*</span>
        </label>
        <input
          id="contact-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={form.contact_phone}
          onChange={(e) => update({ contact_phone: e.target.value })}
          placeholder="e.g. 07700 900123"
          className={`w-full px-3.5 py-3 rounded-xl border transition ${
            form.contact_phone && !phoneOk
              ? "border-red-400 bg-red-50"
              : phoneOk
              ? "border-[var(--brand-green)] bg-[#F4FDF7]"
              : "border-[var(--border-stone)] bg-white"
          }`}
          data-testid="step6-contact-phone-input"
        />
        {form.contact_phone && !phoneOk && (
          <p className="text-xs text-red-600 mt-1">Please enter a valid UK phone number (e.g. 07700 900123).</p>
        )}
      </div>

      <div
        className="rounded-xl p-4 flex items-start gap-3"
        style={{ background: "#F4FDF7", border: "1px solid #CBE7D6" }}
        data-testid="step6-share-notice"
      >
        <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.10)" }}>
          <ShieldCheck size={16} weight="fill" style={{ color: "var(--brand-green)" }} />
        </span>
        <div className="flex-1 text-sm leading-relaxed" style={{ color: "#1F4733" }}>
          <strong>How your details are shared:</strong> your phone number and email stay private
          until you accept a quote. As soon as you do, we'll share them with that one tradesperson
          so they can reach you directly if in-app messaging doesn't get through. Everyone else
          who quoted only sees your first name and rough area.
        </div>
      </div>

      <div className="brand-card p-4 flex items-center justify-between">
        <div>
          <div className="font-bold text-sm text-[var(--brand-green)]">Job alerts</div>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">Get notified when professionals send quotes</p>
        </div>
        <div className="bg-[var(--brand-green)] text-white px-3 py-1 rounded-full text-xs font-bold">ON</div>
      </div>
    </section>
  );
}

function RadioRow({ selected, label, onClick, testId }) {
  return (
    <button type="button" onClick={onClick}
            className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border mb-2 transition text-left ${
              selected ? "bg-[#F4FDF7] border-[var(--brand-green)]" : "border-[var(--border-stone)]"
            }`}
            data-testid={testId}>
      <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? "border-[var(--brand-green)]" : "border-[var(--border-stone)]"}`}>
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-[var(--brand-green)]" />}
      </span>
      <span className="font-semibold text-sm text-[#1F4733]">{label}</span>
    </button>
  );
}

function Step7({ jobId, navigate }) {
  return (
    <section className="mt-2" data-testid="post-job-step-7">
      <div className="rounded-3xl text-white text-center px-6 py-10 relative overflow-hidden"
           style={{ background: "var(--brand-green)" }}>
        {/* Confetti dots */}
        {Array.from({ length: 16 }).map((_, i) => (
          <span key={i} aria-hidden className="absolute rounded-sm"
                style={{
                  left: `${(i * 7 + 8) % 95}%`,
                  top: `${(i * 13 + 5) % 80}%`,
                  width: 6, height: 6,
                  background: i % 2 ? "#FFB073" : "#FFFFFF",
                  transform: `rotate(${i * 23}deg)`,
                  opacity: 0.7,
                }} />
        ))}
        <div className="relative">
          <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center mb-4 shadow-xl">
            <CheckCircle size={36} weight="fill" className="text-[var(--brand-green)]" />
          </div>
          <h2 className="font-display font-extrabold text-2xl">Your job is live!</h2>
          <p className="text-sm text-white/85 mt-2 max-w-sm mx-auto">Professionals in your area have been notified and can now send you quotes.</p>
        </div>
      </div>

      <div className="mt-5 brand-card p-5">
        <div className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-3">What happens next?</div>
        <ul className="space-y-2.5 text-sm text-[var(--text-main)]">
          {["Verified professionals will see your job",
            "They'll send you quotes and messages",
            "You compare and choose the best fit",
            "You get the job done!"].map((line) => (
            <li key={line} className="flex items-start gap-2.5">
              <CheckCircle size={18} weight="fill" className="text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-start gap-2.5 p-3 rounded-lg bg-[#F4FDF7] border border-[#CBE7D6] text-sm">
          <ShieldCheck size={18} weight="duotone" className="text-[var(--brand-green)] flex-shrink-0 mt-0.5" />
          <span className="text-[#1F4733]">We&apos;ll notify you when you receive your first quote.</span>
        </div>
      </div>

      <button onClick={() => navigate(`/jobs/${jobId}`)}
              className="mt-5 w-full bg-[var(--brand-green)] text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-900/10"
              data-testid="step7-view-job">
        View My Job
      </button>
      <button onClick={() => navigate("/")}
              className="mt-2 w-full text-[var(--brand-green)] font-semibold py-3 rounded-xl"
              data-testid="step7-home">
        Back to Home
      </button>
    </section>
  );
}
